import { BookOpenText, Link2, Send } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import { ApiError, type ChatCitation, type ChatTurn } from "@/lib/api";
import { cn } from "@/lib/cn";

/**
 * CHAT-2 on a phone — Ask, which M9.2 called the mobile-first screen and which finally has
 * the surface it was written for.
 *
 * The same streaming call, the same citation chips and the same addresses behind them. What
 * the phone drops is thread management: there is no conversation list here and no "new
 * conversation" button, because the handoff's Ask is one running conversation. Arriving
 * without `?c=` opens the newest thread, so the answer to "what did I ask this morning" is
 * on screen rather than one navigation away — and the address still names the thread, so a
 * link opened on the desktop lands in the same place.
 */
export function PhoneAsk() {
  const { api, conversations, chatAvailable, refreshConversations } = useApp();
  const [params, setParams] = useSearchParams();

  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [question, setQuestion] = useState("");
  const [streaming, setStreaming] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const asked = params.get("c");
  // Newest first, the way the navigator panel lists them. Without a thread in the address
  // the phone opens that one rather than an empty screen.
  const conversationId = asked ?? conversations[0]?.id ?? null;
  const bottom = useRef<HTMLDivElement>(null);

  const load = useCallback(
    async (id: string) => {
      try {
        setTurns(await api.getConversation(id));
        setProblem(null);
      } catch (failure) {
        setProblem(failure instanceof ApiError ? failure.message : "That conversation could not be read.");
      }
    },
    [api],
  );

  useEffect(() => {
    if (!conversationId) {
      setTurns([]);
      return;
    }

    void load(conversationId);
  }, [conversationId, load]);

  useEffect(() => {
    bottom.current?.scrollIntoView?.({ block: "end" });
  }, [turns, streaming]);

  async function ask(event: React.FormEvent) {
    event.preventDefault();

    const text = question.trim();
    if (text.length === 0 || busy) return;

    setBusy(true);
    setProblem(null);
    setStreaming("");

    try {
      const id = conversationId ?? (await api.startConversation()).id;
      if (id !== asked) setParams({ c: id }, { replace: true });

      setQuestion("");

      let answer = "";
      let citations: ChatCitation[] = [];
      let refused: string | null = null;

      await api.ask(id, text, (frame) => {
        if (frame.type === "text") {
          answer += frame.text;
          setStreaming(answer);
        } else if (frame.type === "done") {
          answer = frame.text;
          citations = frame.citations;
        } else {
          refused = frame.message;
        }
      });

      if (refused) {
        setProblem(refused);
        setQuestion(text);
      } else {
        setTurns((current) => [
          ...current,
          turn(current.length, "user", text, []),
          turn(current.length + 1, "assistant", answer, citations),
        ]);
        refreshConversations();
      }
    } catch (failure) {
      setProblem(failure instanceof ApiError ? failure.message : "The answer could not be reached.");
      setQuestion(text);
    } finally {
      setStreaming(null);
      setBusy(false);
    }
  }

  if (!chatAvailable) {
    return (
      <p className="p-4 text-[14px] leading-[1.5] text-[var(--text-secondary)] [text-wrap:pretty]">
        Ask answers from your own material through the same tools an outside agent uses, and this
        deployment has no MCP server configured — so there is nothing for an answer to stand on.
      </p>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="phone-scroll flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto p-4">
        {turns.length === 0 && streaming === null && (
          <p className="m-auto text-center text-[15px] leading-[1.5] text-[var(--text-muted)] [text-wrap:pretty]">
            Ask about a recording, a person or a commitment. Every answer cites where it came from.
          </p>
        )}

        {turns.map((entry) => (
          <Bubble key={entry.id} turn={entry} />
        ))}

        {streaming !== null && (
          <div className="max-w-[86%] self-start rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-3.5 py-[11px]">
            {streaming.length === 0 ? (
              <Typing />
            ) : (
              <p className="whitespace-pre-wrap text-[15px] leading-[1.5] text-[var(--text-primary)]">
                {streaming}
              </p>
            )}
          </div>
        )}

        {problem && (
          <p role="alert" className="text-[13px] text-[var(--warning)]">
            {problem}
          </p>
        )}

        <div ref={bottom} />
      </div>

      <form
        onSubmit={ask}
        className="flex shrink-0 items-center gap-2 border-t border-[var(--border-subtle)] bg-[var(--surface-1)] px-3 py-2.5"
      >
        <input
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          placeholder="Ask Factorium…"
          aria-label="Ask Factorium"
          disabled={busy}
          // 16 px is not a style choice: iOS Safari zooms the whole page when a focused
          // input is any smaller, and the zoom does not come back on blur.
          className="h-11 min-w-0 flex-1 rounded-[22px] border border-[var(--input)] bg-[var(--surface-glass)] px-3.5 text-[16px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)]"
        />
        <button
          type="submit"
          aria-label="Send"
          disabled={busy || question.trim().length === 0}
          className="inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-[var(--primary)] text-[var(--primary-foreground)] disabled:opacity-50"
        >
          <Send size={18} strokeWidth={2} aria-hidden />
        </button>
      </form>
    </div>
  );
}

function Bubble({ turn }: { turn: ChatTurn }) {
  const mine = turn.role === "user";

  return (
    <div className={cn("flex max-w-[86%] flex-col gap-1.5", mine ? "self-end" : "self-start")}>
      <div
        className={cn(
          "rounded-[var(--radius-lg)] px-3.5 py-[11px] text-[15px] leading-[1.5] [text-wrap:pretty]",
          mine
            ? "bg-[var(--primary)] text-[var(--primary-foreground)]"
            : "border border-[var(--border-subtle)] bg-[var(--surface-glass)] text-[var(--text-primary)]",
        )}
      >
        <p className="whitespace-pre-wrap">{turn.content}</p>
      </div>

      {turn.citations.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {turn.citations.map((citation) => (
            <li key={citation.searchDocumentId}>
              <Chip citation={citation} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * A belief goes to the page it lives on. A quote goes to the source it was read from — and
 * a source is a desktop screen, which the phone app answers for rather than pretending
 * otherwise (`phoneSurface.ts`). The chip still points at the truth either way.
 */
function Chip({ citation }: { citation: ChatCitation }) {
  const belief = citation.altitude === "wiki_section";

  const to = belief
    ? `/world-view/${citation.address}`
    : citation.sourceMaterialId
      ? `/sources/${citation.sourceMaterialId}`
      : "/world-view";

  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 rounded-[var(--radius-full)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-2.5 py-1 text-[11px] text-[var(--text-secondary)]"
    >
      {belief ? <BookOpenText size={11} aria-hidden /> : <Link2 size={11} aria-hidden />}
      {citation.title}
    </Link>
  );
}

function Typing() {
  return (
    <span className="flex items-center gap-1" aria-label="Thinking" role="status">
      {[0, 1, 2].map((dot) => (
        <span
          key={dot}
          className="size-[6px] rounded-full bg-[var(--text-muted)]"
          style={{ animation: `omTypingDot 1.2s ${dot * 0.15}s infinite ease-in-out` }}
        />
      ))}
    </span>
  );
}

function turn(
  sequence: number,
  role: "user" | "assistant",
  content: string,
  citations: ChatCitation[],
): ChatTurn {
  return {
    id: `local-${sequence}`,
    sequence,
    role,
    content,
    createdAt: new Date().toISOString(),
    citations,
  };
}
